import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, useLocation } from 'react-router-dom';

import { UiHoverTooltip } from 'src/components/UI/HoverTooltip';
import { UiInnerNotSPALink } from 'src/components/UI/InnerNotSPALink';
import { ROUTE_ORGANIZER_CAMPAIGNS } from 'src/routes';
import {
  ROUTE_ACCOUNT_ORGANIZER_ADMINISTRATORS,
  ROUTE_ACCOUNT_ORGANIZER_CONTRIBUTIONS,
  ROUTE_ACCOUNT_ORGANIZER_DONOR_PROMOCODES,
  ROUTE_ACCOUNT_ORGANIZER_EVENT_GROUP_REGISTRATIONS,
  ROUTE_ACCOUNT_ORGANIZER_EVENT_REGISTRATIONS,
  ROUTE_ACCOUNT_ORGANIZER_EXTERNAL_CONTRIBUTIONS,
  ROUTE_ACCOUNT_ORGANIZER_MANUAL_CONTRIBUTIONS,
  ROUTE_ACCOUNT_ORGANIZER_MEMBERS,
  ROUTE_ACCOUNT_ORGANIZER_PHYSICAL_RAFFLE_TICKETS,
  ROUTE_ACCOUNT_ORGANIZER_PHYSICAL_SWEEPSTAKES_TICKETS,
  ROUTE_ACCOUNT_ORGANIZER_TEAMS,
} from 'src/routes/account/organizer';

export type LayoutHeaderProps = {
  header: string;

  title?: string | null;
  hoverText?: string;
  hoverTextCommonStyle?: boolean;
  isNewDesign?: boolean;
  idForReturning?: number;
};

export const LayoutHeader: FC<LayoutHeaderProps> = ({
  header: headerText,
  title,
  hoverText,
  hoverTextCommonStyle = false,
  isNewDesign = false,
  idForReturning,
}) => {
  const { t } = useTranslation();

  const { pathname } = useLocation();

  /**
   * SPA routes
   * sync with CampaignOverlayContent -> getUrl
   * */
  const isSpaLink = useMemo(
    () =>
      [
        ROUTE_ACCOUNT_ORGANIZER_CONTRIBUTIONS,
        ROUTE_ACCOUNT_ORGANIZER_PHYSICAL_RAFFLE_TICKETS,
        ROUTE_ACCOUNT_ORGANIZER_PHYSICAL_SWEEPSTAKES_TICKETS,
        ROUTE_ACCOUNT_ORGANIZER_EVENT_GROUP_REGISTRATIONS,
        ROUTE_ACCOUNT_ORGANIZER_EXTERNAL_CONTRIBUTIONS,
        ROUTE_ACCOUNT_ORGANIZER_DONOR_PROMOCODES,
        ROUTE_ACCOUNT_ORGANIZER_MANUAL_CONTRIBUTIONS,
        ROUTE_ACCOUNT_ORGANIZER_MEMBERS,
        ROUTE_ACCOUNT_ORGANIZER_TEAMS,
        ROUTE_ACCOUNT_ORGANIZER_EVENT_REGISTRATIONS,
        ROUTE_ACCOUNT_ORGANIZER_ADMINISTRATORS,
      ].some((path) => !!matchPath(pathname, path)),
    [pathname]
  );

  const header = (
    <>
      {headerText}
      {hoverText && (
        <>
          &nbsp;
          <UiHoverTooltip noWrapper place="bottom" text={hoverText} hoverTextCommonStyle={hoverTextCommonStyle}>
            <i className="material-icons">info_outline</i>
          </UiHoverTooltip>
        </>
      )}
    </>
  );

  return isNewDesign ? (
    <div className="account-management-page-header-container">
      <div className="account-management-page-header PrimaryPageBackgroundTextColor">
        <div className="account-setup-page-title">{header}</div>
        {title && (
          <>
            <span className="separator account-setup-title-text">|</span>
            <span className="account-setup-title-text">{title}</span>
          </>
        )}
      </div>
    </div>
  ) : (
    <div className="account-management-page-header">
      {idForReturning && (
        <div className="back-to-campaign-button-container">
          {isSpaLink ? (
            <Link
              to={`${ROUTE_ORGANIZER_CAMPAIGNS}?openedCampaignId=${idForReturning}`}
              className="LinkTextColor back-to-campaign-button"
            >
              <i className="material-icons back-button-icon">keyboard_backspace</i>
              {t('common.back')}
            </Link>
          ) : (
            <UiInnerNotSPALink
              href={`${ROUTE_ORGANIZER_CAMPAIGNS}?openedCampaignId=${idForReturning}`}
              className="LinkTextColor back-to-campaign-button"
            >
              <i className="material-icons back-button-icon">keyboard_backspace</i>
              {t('common.back')}
            </UiInnerNotSPALink>
          )}
        </div>
      )}
      <h1 className="account-management-page-title HeaderTextColor">{header}</h1>
      {title && (
        <h5 className="account-management-campaign-title">
          <span className="separator">|</span>&nbsp;
          <span className="account-management-campaign-title-text PageTitleTextWithoutBackgroundColor">{title}</span>
        </h5>
      )}
    </div>
  );
};
